<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import StatusBadge from '../shared/StatusBadge.svelte';
  import { fetchLambdaConfigs, type LambdaConfig } from '../../services/lambda-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let functions = $state<LambdaConfig[]>([]);
  let loading = $state(true);
  let expandedFn = $state<string | null>(null);

  async function load() {
    loading = true;
    try {
      functions = await fetchLambdaConfigs(env);
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    env;
    load();
  });

  function formatBytes(bytes: number): string {
    if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(0)} KB`;
    return `${(bytes / 1_000_000).toFixed(1)} MB`;
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  const runtimeColors: Record<string, string> = {
    'nodejs20.x': 'bg-success/15 text-success border-success/30',
    'python3.12': 'bg-info/15 text-info border-info/30',
    'java21': 'bg-warning/15 text-warning border-warning/30',
  };
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'Lambda Inspector' }]} />
      <EnvSwitcher bind:value={env} />
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">λ Lambda Inspector</h1>
      <p class="mt-1 text-sm text-text-secondary">View Lambda configurations, runtimes, and environment variables</p>
    </div>

    {#if loading}
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {#each Array(7) as _, i (i)}
          <div class="h-48 rounded-xl bg-bg-panel animate-skeleton-pulse"></div>
        {/each}
      </div>
    {:else}
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {#each functions as fn (fn.functionName)}
          {@const isExpanded = expandedFn === fn.functionName}
          <div
            class="bg-bg-panel border rounded-xl overflow-hidden transition-all cursor-pointer hover:border-accent/50 {isExpanded ? 'border-accent col-span-1 md:col-span-2 xl:col-span-3' : 'border-border'}"
          >
            <button
              onclick={() => expandedFn = isExpanded ? null : fn.functionName}
              class="w-full text-left p-5"
            >
              <div class="flex items-start justify-between mb-3">
                <h3 class="font-semibold text-sm font-mono">{fn.functionName}</h3>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border {runtimeColors[fn.runtime] || 'bg-bg-card text-text-secondary border-border'}">
                  {fn.runtime}
                </span>
              </div>
              <div class="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-text-muted">Memory</p>
                  <p class="font-semibold">{fn.memory} MB</p>
                </div>
                <div>
                  <p class="text-text-muted">Timeout</p>
                  <p class="font-semibold">{fn.timeout}s</p>
                </div>
                <div>
                  <p class="text-text-muted">Code Size</p>
                  <p class="font-semibold">{formatBytes(fn.codeSize)}</p>
                </div>
              </div>
              <p class="text-[10px] text-text-muted mt-3">Modified: {formatDate(fn.lastModified)}</p>
            </button>

            {#if isExpanded}
              <div class="border-t border-border px-5 pb-5 pt-4 animate-fade-in-up">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <!-- Env Vars -->
                  <div>
                    <h4 class="text-xs font-medium text-text-secondary mb-2">Environment Variables</h4>
                    <div class="space-y-1">
                      {#each Object.entries(fn.envVars) as [key, val]}
                        <div class="flex gap-2 text-xs font-mono">
                          <span class="text-accent shrink-0">{key}</span>
                          <span class="text-text-muted">=</span>
                          <span class="text-text-primary break-all">{val}</span>
                        </div>
                      {/each}
                    </div>
                  </div>

                  <!-- Layers -->
                  <div>
                    <h4 class="text-xs font-medium text-text-secondary mb-2">Layers ({fn.layers.length})</h4>
                    {#if fn.layers.length === 0}
                      <p class="text-xs text-text-muted">No layers</p>
                    {:else}
                      <div class="space-y-1">
                        {#each fn.layers as layer}
                          <p class="text-xs font-mono text-text-primary break-all">{layer.split(':layer:')[1] || layer}</p>
                        {/each}
                      </div>
                    {/if}
                  </div>

                  <!-- VPC -->
                  <div>
                    <h4 class="text-xs font-medium text-text-secondary mb-2">VPC Config</h4>
                    {#if fn.vpcConfig}
                      <div class="text-xs space-y-2">
                        <div>
                          <p class="text-text-muted mb-0.5">Subnets</p>
                          {#each fn.vpcConfig.subnetIds as s}
                            <p class="font-mono text-text-primary">{s}</p>
                          {/each}
                        </div>
                        <div>
                          <p class="text-text-muted mb-0.5">Security Groups</p>
                          {#each fn.vpcConfig.securityGroupIds as sg}
                            <p class="font-mono text-text-primary">{sg}</p>
                          {/each}
                        </div>
                      </div>
                    {:else}
                      <StatusBadge status="info" label="No VPC" size="sm" />
                    {/if}
                  </div>
                </div>

                <div class="mt-4 pt-3 border-t border-border-light">
                  <p class="text-xs text-text-muted font-mono">Handler: {fn.handler}</p>
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>
